import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DisplayListRow } from '../../interfaces/display-list-row.interface';
import { FilterOutput } from '../../interfaces/filter-outpt.interface';
import { FilterQueryValue } from '../../interfaces/filter-query-value.inteface';
import { ListConfig } from '../../interfaces/list-config.interface';
import { DataService } from '../../services/data/data.service';
import { FiltersService } from '../../services/filters/filters.service';
import { Utils } from '../../utils/utils.class';
import { ProcessAction } from '../../interfaces/process-action.interface';
import { ProcessEntry } from '../../interfaces/screen-actions.interface';
import { CreatorDataSource } from '../../data-sources/creator-data-source';
import { CustomDataSource } from '../../data-sources/custom-data-source';
import { FilterSet } from '../../interfaces/filter-set.interface';
import { Filter } from '../table/interfaces/filter.interface';

@Component({
  selector: 'sellions-layout-renderer-list',
  templateUrl: './list.component.html',
})
export class ListRendererComponent implements OnInit, OnDestroy {
  @ViewChild('itemList') itemList: ElementRef;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() config: ListConfig;
  @Input() database: any;
  @Input() searchQuery: string;
  @Input() sortByKey: string;
  @Input() sortByDirection: 'ASC' | 'DESC';
  @Input() baseUrl: string;
  @Input() emptyListMessage: string;
  @Input() dataProccessingFunction: (data: any) => any = (data: any) => data;
  @Input() urlParams: { [key: string]: string };
  @Input() paginationEnabled: boolean = true;
  @Input() page: number = 0;
  @Input() size: number = 1000;
  @Input() sizes: number[] = [5, 10, 15];
  @Input() params: any;

  @Output() navigationEvent = new EventEmitter<{
    viewId: string;
    element?: Object;
    objectKey?: Object;
    viewComponent?: string;
  }>();
  @Output() loadingEvent = new EventEmitter<boolean>();
  @Output() elementClicked = new EventEmitter<{
    element: Object;
    target: EventTarget;
  }>();
  @Output() receivedData = new EventEmitter<any[]>();
  @Output() contextMenuAction = new EventEmitter<ProcessAction>();

  public loading: boolean;
  private unsubscribe$ = new Subject<void>();
  public data: any[] = [];
  public totalElements: number = 0;
  private dataSource: CustomDataSource;
  private filterSet: any;

  constructor(
    private dataService: DataService,
    private filtersService: FiltersService,
    private creatorDataSource: CreatorDataSource,
    private elementRef: ElementRef,
  ) {}

  ngOnInit() {
    if (this.config.displayConfig.pagination !== undefined) {
      this.paginationEnabled = this.config.displayConfig.pagination;
    }
    this.dataSource = this.creatorDataSource.create(this.config.dataSourceConfig);
    this.dataSource.loading$.pipe(takeUntil(this.unsubscribe$)).subscribe((loading) => this.loadingEvent.emit(loading));
    this.dataSource.totalNumberOfElements$.pipe(takeUntil(this.unsubscribe$)).subscribe((number) => (this.totalElements = number));
    this.dataSource.dataInitialized$.pipe(takeUntil(this.unsubscribe$)).subscribe((status) => {
      if (status) {
        const data = this.dataSource.getData();
        this.data = this.processData(data);
        this.receivedData.emit(this.data);
      }
    });
    this.filtersService.getFilters(this.config.name)
      .then(filterSet => {
        this.filterSet = filterSet;
        this.filtersService.filterSetSubject$.pipe(takeUntil(this.unsubscribe$)).subscribe(
          (filterSet: FilterSet) => {
            if (!filterSet || filterSet.name !== this.config.name) {
              return;
            }
            this.reload();
          }
        );
      })
      // CATCH + THEN TO SIMULATE FINALLY
      .catch((error) => {console.error(error)})
      .then(() => {
        this.reload();
      });

  }

  ngAfterViewInit() {
    this.setDotColor();
    if (this.paginator) this.paginator.page.pipe(takeUntil(this.unsubscribe$)).subscribe((page) => this.reload());
  }

  private setDotColor(): void {
    if (this.config.displayConfig.rowLayoutMappings.dotColor) {
      this.elementRef.nativeElement.style.setProperty('--dot-color', this.config.displayConfig.rowLayoutMappings.dotColor);
    }
  }

  public reload() {
    if (!this.paginator) {
      this.loadData();
      return;
    }
    this.loadData(this.paginator.pageIndex, this.paginator.pageSize);
  }

  public loadData(pageIndex?: number, pageSize?: number) {
    const config = this.config.dataSourceConfig;
    const filterSet = this.filtersService.filterSetSubject$.value ? this.filtersService.filterSetSubject$.value : this.filterSet;
    let filtersOutput = filterSet && filterSet.filters;
    const dataSourceFilters = this.getDataSourceFilterOutputs();
    filtersOutput = this.combineFilters(dataSourceFilters, filtersOutput);
    const url = this.getDataUrl(filtersOutput, this.searchQuery);
    const filters = filtersOutput.map((filter) => {
      return { key: filter.column, value: filter.value, type: filter.type } as Filter;
    });
    const sortConfiguration = {
      column: config.sortProperty,
      direction: config.sortDirection
    }
    if(filterSet && filterSet.sort && filterSet.sort.column) {
      sortConfiguration.column = filterSet.sort.column;
      sortConfiguration.direction = filterSet.sort.direction;
    } else if(filterSet && filterSet.filterConfig && filterSet.filterConfig.defaultSort) {
      sortConfiguration.column = filterSet.filterConfig.defaultSort.column;
      sortConfiguration.direction = filterSet.filterConfig.defaultSort.direction;
    }

    this.dataSource.loadData({
      page: pageIndex || 0,
      size: pageSize || this.size,
      url: url,
      token: config.token,
      offlineStorageKey: config.offlineStorageKey,
      data: config.data,
      sortProperty: sortConfiguration.column,
      sortDirection: sortConfiguration.direction,
      //TODO wyrównać filtry dataView vs. endpoint-y
      filters: filters,
      searchQuery: this.searchQuery,
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private getDataSourceFilterOutputs(): FilterOutput[] {
    const filters = this.config.dataSourceConfig.filters || [];

    return filters.map((filter) => {
      const queryFilter = { ...filter };
      queryFilter.value = Utils.evaluateVariablesString(filter.value, this.params);
      return {
        type: filter.type,
        value: filter.value,
        column: filter.column,
        queryValue: queryFilter,
      };
    });
  }

  public getParams() {
    return this.params;
  }

  private combineFilters(dataSourceFilters: FilterOutput[], filters?: FilterOutput[]): FilterOutput[] {
    if (!filters) {
      filters = [];
    }
    return dataSourceFilters.concat(filters);
  }

  private getDataUrl(filters?: FilterOutput[], searchQuery?: string): string {
    let url = this.dataService.getUrl(this.baseUrl, this.config.dataSourceConfig.onlineDataSourceUrl);
    url = Utils.evaluateVariablesString(url, this.params);
    url = this.addParamsToUrl(url, filters, searchQuery);

    return url;
  }

  private getParamsAsString(): string {
    let str = '';
    Object.keys(this.urlParams).forEach((param) => (str += param + '=' + this.urlParams[param] + '&'));
    return str.slice(0, -1);
  }

  private addParamsToUrl(url: string, filters?: FilterOutput[], searchQuery?: string): string {
    if (filters) {
      url += this.generateFilterUrl(filters);
    }

    if (searchQuery) {
      url += this.generateSearchUrl(url, searchQuery);
    }

    if (this.urlParams) {
      url = this.appendSeparator(url) + this.getParamsAsString();
    }

    return url;
  }

  private appendSeparator(url: string): string {
    const separator = url.indexOf('?') === -1 ? '?' : '&';
    return url + separator;
  }

  private generateFilterUrl(filters?: FilterOutput[]): string {
    const queryFilters: FilterQueryValue[] = [];
    filters.forEach((filter) => {
      if (filter.queryValue.value) {
        const sameColumnFilterIndex = queryFilters.findIndex((f) => f.column === filter.column);

        if (sameColumnFilterIndex > -1) {
          if (queryFilters[sameColumnFilterIndex].type === 'COMPOUND') {
            queryFilters[sameColumnFilterIndex].subFilters.push(filter.queryValue);
          } else {
            const sameColumnFilter = queryFilters.splice(sameColumnFilterIndex, 1);
            const compoundFilter = this.createCompoundFilter(...sameColumnFilter, filter.queryValue);
            queryFilters.push(compoundFilter);
          }
        } else {
          queryFilters.push(filter.queryValue);
        }
      }
    });

    if (queryFilters.length < 1) {
      return '';
    }

    return '?filters=' + encodeURIComponent(JSON.stringify({ filters: queryFilters }));
  }

  private createCompoundFilter(...filters: FilterQueryValue[]): FilterQueryValue {
    return {
      column: filters[0].column, // For contact purposes only - the backend ignores this value for the COMPOUND filter
      type: 'COMPOUND',
      subFilters: filters,
    };
  }

  private generateSearchUrl(url: string, searchQuery: string): string {
    if (url.includes('?')) {
      return '&searchQuery=' + searchQuery;
    }

    return '?searchQuery=' + searchQuery;
  }

  private processData(elements: Object[]): Object[] {
    const processedData = elements.map(
      (element) => this.evaluateDisplayStrings(this.dataProccessingFunction ? this.dataProccessingFunction(element) : element) as any,
    );
    return processedData;
  }

  private evaluateDisplayStrings(object: DisplayListRow): Object {
    object.title = Utils.evaluateString(this.config.displayConfig.rowLayoutMappings.title, object);
    object.subtitle = Utils.evaluateString(this.config.displayConfig.rowLayoutMappings.subtitle, object);
    object.content = Utils.evaluateString(this.config.displayConfig.rowLayoutMappings.content, object);
    if (this.config.displayConfig.rowLayoutMappings.img) {
      object.img = Utils.evaluateString(this.config.displayConfig.rowLayoutMappings.img, object);
    }

    return object;
  }

  public onClick(element: any, $event: MouseEvent) {
    if (!this.config.onClick) {
      return;
    }

    if ($event.target instanceof HTMLAnchorElement) {
      const htmlElement: HTMLAnchorElement = $event.target;
      const href: string = htmlElement.href;
      const isSpecialAction = href && (href.startsWith('mailto:') || href.startsWith('tel:'));
      if (isSpecialAction) {
        return;
      }
    }

    if (this.config.onClick.behaviour === 'CHANGE_VIEW') {
      this.navigationEvent.emit({
        viewId: this.config.onClick.viewId,
        element,
        viewComponent: this.config.onClick.viewComponent,
      });
      return;
    }

    if (this.config.onClick.behaviour === 'TRIGGER_ELEMENT_CLICK') {
      this.elementClicked.emit({ element, target: $event.target });
      return;
    }

    console.debug(`Error! Behaviour not implemented.`, this.config.onClick);
  }

  public onContextMenuClicked(element: any, process: ProcessEntry) {
    this.contextMenuAction.emit({ process: process, element: element });
  }

  isContextMenuVisible(): boolean {
    return (
      this.config &&
      this.config.screenActions &&
      this.config.screenActions.processList &&
      this.config.screenActions.processList.find((process) => process.contextMenuVisible) != null
    );
  }
}



// WEBPACK FOOTER //
// ./src/submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/components/list/list.component.ts